'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

const COMPLIANCE_SITES = [
  { site: 'Site Alpha', score: 94, items: 18, expired: 1, expiring: 2 },
  { site: 'Site Beta', score: 87, items: 14, expired: 0, expiring: 3 },
  { site: 'Warehouse B', score: 100, items: 9, expired: 0, expiring: 0 },
  { site: 'Site Gamma', score: 78, items: 22, expired: 2, expiring: 4 },
];

const EXPIRY_ITEMS = [
  { item: 'Full Body Safety Harness', batch: 'SH-2024-07', site: 'Site Alpha', daysLeft: -2, status: 'EXPIRED' as const },
  { item: 'Fire Extinguisher (CO₂ 5kg)', batch: 'FE-2026-03', site: 'Site Beta', daysLeft: 4, status: 'EXPIRING_SOON' as const },
  { item: 'First Aid Kit (Type B)', batch: 'FAK-2026-01', site: 'Warehouse B', daysLeft: 9, status: 'EXPIRING_SOON' as const },
  { item: 'Gas Detector (H₂S)', batch: 'GD-2025-12', site: 'Site Gamma', daysLeft: 14, status: 'EXPIRING_SOON' as const },
  { item: 'Hard Hat EN 397', batch: 'HH-2026-02', site: 'Site Alpha', daysLeft: 21, status: 'EXPIRING_SOON' as const },
];

const CALENDAR = [
  { date: '01 Aug', item: 'Gas Detector Calibration', site: 'Site Gamma', type: 'inspection' },
  { date: '05 Aug', item: 'Fire Extinguisher Service', site: 'Site Beta', type: 'service' },
  { date: '10 Aug', item: 'PPE Batch Renewal', site: 'Site Alpha', type: 'renewal' },
  { date: '15 Aug', item: 'Safety Harness Inspection', site: 'Warehouse B', type: 'inspection' },
];

const PIE_DATA = [
  { name: 'All OK', value: 48, color: '#167A6E' },
  { name: 'Expiring', value: 9, color: '#E09B28' },
  { name: 'Expired', value: 3, color: '#DC2626' },
  { name: 'Inspection Due', value: 4, color: '#7C3AED' },
];

export default function QHSEManagerDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-teal-700 to-teal-900 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="ShieldCheckIcon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">QHSE Compliance Dashboard</p>
          <p className="text-[12px] text-white/65">QHSE Manager · Cross-site read access · All 4 sites</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link href="/qhse" className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="ClipboardDocumentListIcon" size={13} /> QHSE Register
          </Link>
          <Link href="/qhse/report" className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground hover:bg-amber-400 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="DocumentChartBarIcon" size={13} /> Compliance Report
          </Link>
        </div>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Expired', value: '3', sub: 'Immediate action', color: 'bg-red-50 border-red-300', icon: 'XCircleIcon', iconColor: 'bg-red-100 text-red-600', pulse: true },
          { label: 'Expiring Soon', value: '9', sub: 'Within 30 days', color: 'bg-amber-50 border-amber-300', icon: 'ClockIcon', iconColor: 'bg-amber-100 text-amber-600', pulse: false },
          { label: 'Inspection Due', value: '4', sub: 'Overdue or today', color: 'bg-purple-50 border-purple-200', icon: 'CalendarDaysIcon', iconColor: 'bg-purple-100 text-purple-600', pulse: false },
          { label: 'All OK', value: '48', sub: 'Compliant items', color: 'bg-teal/5 border-teal/25', icon: 'ShieldCheckIcon', iconColor: 'bg-teal/10 text-teal', pulse: false },
        ].map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.iconColor}`}>
                <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
              </div>
              {k.pulse && <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse mt-1" />}
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-3xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Pie chart + Site scores */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 bg-card border border-border rounded-2xl shadow-card p-5">
          <h3 className="text-sm font-600 text-foreground mb-1">Compliance Breakdown</h3>
          <p className="text-[11px] text-muted-foreground mb-4">64 total QHSE items across all sites</p>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={PIE_DATA} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                {PIE_DATA.map((entry, i) => <Cell key={`cell-${i}`} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number, name: string) => [v, name]} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #D1D9E6' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {PIE_DATA.map((d) => (
              <div key={d.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                <span className="text-[11px] text-muted-foreground">{d.name}: <span className="font-600 text-foreground">{d.value}</span></span>
              </div>
            ))}
          </div>
        </div>

        <div className="xl:col-span-3 bg-card border border-border rounded-2xl shadow-card p-5">
          <h3 className="text-sm font-600 text-foreground mb-4">Site-wise Compliance Score</h3>
          <div className="space-y-4">
            {COMPLIANCE_SITES.map((s) => (
              <div key={s.site}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-600 text-foreground">{s.site}</span>
                    {s.expired > 0 && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-700">{s.expired} EXPIRED</span>}
                    {s.expiring > 0 && <span className="text-[9px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded font-600">{s.expiring} expiring</span>}
                  </div>
                  <span className={`text-[13px] font-700 tabular-nums ${s.score >= 95 ? 'text-teal' : s.score >= 80 ? 'text-amber-600' : 'text-red-600'}`}>{s.score}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${s.score >= 95 ? 'bg-teal' : s.score >= 80 ? 'bg-amber-400' : 'bg-red-500'}`}
                    style={{ width: `${s.score}%` }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{s.items} items tracked</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expiry list + Calendar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="ShieldExclamationIcon" size={15} className="text-red-500" />
              <h3 className="text-sm font-600 text-foreground">Expiry Alerts</h3>
            </div>
            <Link href="/qhse" className="text-[11px] text-secondary hover:underline">Full register</Link>
          </div>
          <div className="divide-y divide-border">
            {EXPIRY_ITEMS.map((a) => (
              <div key={a.batch} className={`flex items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors ${a.daysLeft < 0 ? 'bg-red-50/50' : ''}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.daysLeft < 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <Icon name="ShieldExclamationIcon" size={16} className={a.daysLeft < 0 ? 'text-red-600' : 'text-amber-600'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-600 text-foreground truncate">{a.item}</p>
                  <p className="text-[10px] text-muted-foreground">Batch {a.batch} · {a.site}</p>
                </div>
                <div className="text-right shrink-0">
                  {a.daysLeft < 0
                    ? <span className="text-[11px] font-700 text-red-600">Expired {Math.abs(a.daysLeft)}d ago</span>
                    : <span className="text-[11px] font-700 text-amber-600">Expires in {a.daysLeft}d</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="CalendarDaysIcon" size={15} className="text-secondary" />
              <h3 className="text-sm font-600 text-foreground">Compliance Calendar — Next 30 Days</h3>
            </div>
          </div>
          <div className="divide-y divide-border">
            {CALENDAR.map((c) => (
              <div key={c.date} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="w-14 text-center shrink-0">
                  <p className="text-[11px] font-700 text-secondary">{c.date}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-600 text-foreground">{c.item}</p>
                  <p className="text-[10px] text-muted-foreground">{c.site}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 ${
                  c.type === 'inspection' ? 'bg-purple-100 text-purple-700' :
                  c.type === 'service'? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                }`}>{c.type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
